"use client"

import { Input } from "@/components/ui/input"

interface VerificationTabsProps {
  value: "account-number" | "account-name" | "both"
  onValueChange: (value: "account-number" | "account-name" | "both") => void
  thaiName?: string
  englishName?: string
  onThaiNameChange?: (value: string) => void
  onEnglishNameChange?: (value: string) => void
  accountNumber?: string
  onAccountNumberChange?: (value: string) => void
  errors?: {
    account_number?: string
    account_name_th?: string
    account_name_en?: string
  }
}

export function VerificationTabs({ 
  value, 
  onValueChange,
  thaiName = "",
  englishName = "",
  onThaiNameChange,
  onEnglishNameChange,
  accountNumber = "",
  onAccountNumberChange,
}: VerificationTabsProps) {
  return (
    <div className="mb-4">
      <label className="text-sm font-medium text-gray-900 mb-3 block">
        รูปแบบการตรวจสอบบัญชี
      </label>
      <div className="grid grid-cols-3 gap-3 border rounded-lg border-gray-400 p-2">
        <button
          onClick={() => onValueChange("account-number")}
          className={`flex flex-col items-center gap-2 p-3 rounded-lg ${
            value === "account-number"
              ? "text-brand-primary"
              : ""
          }`}
        >
          <svg width="46" height="36" viewBox="0 0 46 36" fill="none">
            <path
              d="M39.5 18.6667V22.8333M20.75 6.16667H4C2.89543 6.16667 2 7.0621 2 8.16667V22.8333M2 22.8333V31.25C2 32.3546 2.89543 33.25 4 33.25H37.5C38.6046 33.25 39.5 32.3546 39.5 31.25V22.8333M2 22.8333H39.5M43.6667 2L33.25 12.4167L29.0833 8.25"
              stroke="#66BB6A"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="text-xs font-medium">ตรวจเลขบัญชี</span>
        </button>

        <button
          onClick={() => onValueChange("account-name")}
          className={`flex flex-col items-center gap-2 p-3 rounded-lg ${
            value === "account-name"
              ? "text-brand-primary"
              : ""
          }`}
        >
          <svg width="46" height="36" viewBox="0 0 46 36" fill="none">
            <path
              d="M39.5 18.6667V22.8333M20.75 6.16667H4C2.89543 6.16667 2 7.0621 2 8.16667V22.8333M2 22.8333V31.25C2 32.3546 2.89543 33.25 4 33.25H37.5C38.6046 33.25 39.5 32.3546 39.5 31.25V22.8333M2 22.8333H39.5M43.6667 2L33.25 12.4167L29.0833 8.25"
              stroke="#66BB6A"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="text-xs font-medium">ตรวจชื่อบัญชี</span>
        </button>

        <button
          onClick={() => onValueChange("both")}
          className={`flex flex-col items-center gap-2 p-3 rounded-lg ${
            value === "both"
              ? "text-brand-primary"
              : ""
          }`}
        >
          <svg width="46" height="36" viewBox="0 0 46 36" fill="none">
            <path
              d="M39.5 18.6667V22.8333M20.75 6.16667H4C2.89543 6.16667 2 7.0621 2 8.16667V22.8333M2 22.8333V31.25C2 32.3546 2.89543 33.25 4 33.25H37.5C38.6046 33.25 39.5 32.3546 39.5 31.25V22.8333M2 22.8333H39.5M43.6667 2L33.25 12.4167L29.0833 8.25"
              stroke="#66BB6A"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="text-xs font-medium">ตรวจทั้งหมด</span>
        </button>
      </div>

      {/* Show input field when account-number tab is selected */}
      {value === "account-number" && (
        <div className="mt-4">
          <label className="text-sm font-medium text-gray-900 mb-2 block">
            เลขที่บัญชีธนาคาร<span className="text-red-500">*</span>
          </label>
          <Input
            placeholder="กรอกเลขที่บัญชีธนาคาร"
            value={accountNumber}
            onChange={(e) => onAccountNumberChange?.(e.target.value)}
            className="w-full"
          />
        </div>
      )}

      {/* Show input fields when account-name tab is selected */}
      {value === "account-name" && (
        <div className="mt-4 space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-900 mb-2 block">
              ชื่อ-นามสกุล ภาษาไทย (ห้ามใส่คำนำหน้า)*
            </label>
            <Input
              placeholder="กรอกชื่อนามสกุล ภาษาไทย"
              value={thaiName}
              onChange={(e) => onThaiNameChange?.(e.target.value)}
              className="w-full"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-900 mb-2 block">
              ชื่อ-นามสกุล ภาษาอังกฤษ (ห้ามใส่คำนำหน้า)*
            </label>
            <Input
              placeholder="กรอกชื่อนามสกุล ภาษาอังกฤษ"
              value={englishName}
              onChange={(e) => onEnglishNameChange?.(e.target.value)}
              className="w-full"
            />
          </div>
        </div>
      )}

      {/* Show input fields when all tab is selected */}
      {value === "both" && (
        <div className="mt-4 space-y-4">
          <div className="mt-4">
            <label className="text-sm font-medium text-gray-900 mb-2 block">
              เลขที่บัญชีธนาคาร<span className="text-red-500">*</span>
            </label>
            <Input
              placeholder="กรอกเลขที่บัญชีธนาคาร"
              value={accountNumber}
              onChange={(e) => onAccountNumberChange?.(e.target.value)}
              className="w-full"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-900 mb-2 block">
              ชื่อ-นามสกุล ภาษาไทย (ห้ามใส่คำนำหน้า)*
            </label>
            <Input
              placeholder="กรอกชื่อนามสกุล ภาษาไทย"
              value={thaiName}
              onChange={(e) => onThaiNameChange?.(e.target.value)}
              className="w-full"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-900 mb-2 block">
              ชื่อ-นามสกุล ภาษาอังกฤษ (ห้ามใส่คำนำหน้า)*
            </label>
            <Input
              placeholder="กรอกชื่อนามสกุล ภาษาอังกฤษ"
              value={englishName}
              onChange={(e) => onEnglishNameChange?.(e.target.value)}
              className="w-full"
            />
          </div>
        </div>
      )}
    </div>
  )
}
